import { Injectable } from "@angular/core";

export interface TitleValidationResult {
  isValid: boolean;
  message: string;
  errorCode: "format" | "general" | null;
}

@Injectable({
  providedIn: "root",
})
export class TitleValidationService {
  private readonly defaultTitle = "Unnamed Test";
  private readonly defaultTitleNormalized = "unnamedtest";
  private readonly minLength = 3;
  private readonly maxLength = 80;
  private readonly htmlTagRegex = /<\/?[a-z][^>]*>/i;
  private readonly htmlEntityTagRegex = /&lt;\/?[a-z][\s\S]*?&gt;/i;

  private readonly genericValidationMessage =
    "Please enter a test name that is unique and between 3 to 80 characters.<br/>" +
    "Note that certain special characters i.e. /     ;  : and < in the title will not be displayed in the docx/qti filename.";

  private readonly invalidFormatMessage = "Invalid title format.";

  public validateTitle(title: string): TitleValidationResult {
    const rawTitle = (title || "").toString();
    const trimmedTitle = rawTitle.trim();

    if (this.containsHtmlTag(rawTitle)) {
      return {
        isValid: false,
        message: this.invalidFormatMessage,
        errorCode: "format",
      };
    }

    if (
      trimmedTitle.length < this.minLength ||
      trimmedTitle.length > this.maxLength ||
      trimmedTitle.length === 0 ||
      this.isDefaultTitle(rawTitle)
    ) {
      return {
        isValid: false,
        message: this.genericValidationMessage,
        errorCode: "general",
      };
    }

    return {
      isValid: true,
      message: "",
      errorCode: null,
    };
  }

  public containsHtmlTag(title: string): boolean {
    const rawTitle = (title || "").toString();

    // Treat bracketed/encoded HTML-like input as invalid title format.
    return (
      this.htmlTagRegex.test(rawTitle) ||
      this.htmlEntityTagRegex.test(rawTitle) ||
      rawTitle.includes("<") ||
      rawTitle.includes(">")
    );
  }

  public isDefaultTitle(title: string): boolean {
    const normalizedTitle = (title || "")
      .toString()
      .replace(/\s+/g, "")
      .toLowerCase();

    return normalizedTitle === this.defaultTitleNormalized || title === this.defaultTitle;
  }
}